import CircularProgress from '@mui/material/CircularProgress';
import Grid from '@mui/material/Grid';
import { OptionsObject, useSnackbar } from 'notistack';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { yupResolver } from '@hookform/resolvers/yup';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import routes from '../../config/routes';
import { MonthClosingActionType, userInfoNameSelector } from '../../state';
import { AppPermissions, CloseMonthForm } from '../../types';
import { PermissionsProvider } from '../Permissions';
import { DatePickerFlexSalesView } from '../DatePickerFlexSales';
import {
  CountrySelectFormInput,
  DatePickerFormInput,
  FlexSalesForm,
  FlexSalesFormActions,
} from '../FlexSalesForm';
import { useListener } from '../../hooks';
import { getSchema, maxDate, minDate } from './schema';
import DialogFlexSales from '../DialogFlexSales';

const messageOptions: OptionsObject = {
  variant: 'success',
  autoHideDuration: 6000,
  anchorOrigin: { vertical: 'bottom', horizontal: 'right' },
};

const getDefaultValues = (createdBy: string): CloseMonthForm => ({
  country: '',
  accountingMonth: maxDate,
  createdBy,
});

const views: DatePickerFlexSalesView[] = ['year', 'month'];

const MonthClosingForm = () => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const resolver = useMemo(() => yupResolver(getSchema()), []);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const userName = useSelector(userInfoNameSelector);
  const defaultValues = useMemo(() => getDefaultValues(userName as never), [userName]);

  const formListener = useListener(
    MonthClosingActionType.monthClosingCloseRequested,
    MonthClosingActionType.monthClosingCloseSuccess,
    MonthClosingActionType.monthClosingCloseFailure
  );
  const navigate = useNavigate();

  const handleCancel = useCallback(() => {
    navigate(routes.protectedNestedRoutes.monthClosing.index.path);
  }, [navigate]);

  const [open, setOpen] = useState(false);
  const [countryConfirmContent, setCountry] = useState('');
  const [monthConfirmContent, setMonth] = useState('');
  const [submittedData, setSubmittedData] = useState<CloseMonthForm | null>(null);

  const handleConfirmDialogOpenClick = useCallback(() => setOpen(true), []);
  const handleConfirmDialogClose = useCallback(() => setOpen(false), []);

  const closeForm = () => {
    setSubmittedData(null);
    handleConfirmDialogClose();
    handleCancel();
  };

  const handleConfirmDialogApply = async () => {
    if (!submittedData) return;

    try {
      setIsSubmitting(true);
      await formListener(submittedData);

      enqueueSnackbar(t('monthClosing.messages.closeSuccess'), messageOptions);
    } catch (error) {
      enqueueSnackbar(t('monthClosing.messages.closeError'), { variant: 'error' });
    } finally {
      closeForm();
      setIsSubmitting(false);
    }
  };

  const onSubmit = (data: CloseMonthForm) => {
    setSubmittedData(data);
    handleConfirmDialogOpenClick();
    setCountry(data.country);
    const month = data.accountingMonth
      ? data.accountingMonth.toLocaleString('default', { month: '2-digit' })
      : '00';
    const year = data.accountingMonth
      ? data.accountingMonth.toLocaleString('default', { year: 'numeric' })
      : '0000';
    const formattedDate = `${month}-${year}`;
    setMonth(formattedDate);
  };

  return (
    <PermissionsProvider resourcePermissions={[AppPermissions.MONTH_CLOSING]}>
      {open && (
        <DialogFlexSales onClose={handleConfirmDialogClose} open scroll="paper">
          <DialogTitle>{t('dialogs.closingConfirm.title')}</DialogTitle>
          <DialogContent>
            {t('dialogs.closingConfirm.message', {
              country: countryConfirmContent,
              month: monthConfirmContent,
            })}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleConfirmDialogClose} disabled={isSubmitting}>
              {t('dialogs.closingConfirm.cancelButton')}
            </Button>

            <Button onClick={handleConfirmDialogApply} disabled={isSubmitting}>
              {isSubmitting ? (
                <CircularProgress size={20} />
              ) : (
                t('dialogs.closingConfirm.confirmButton')
              )}
            </Button>
          </DialogActions>
        </DialogFlexSales>
      )}
      <FlexSalesForm onValidSubmit={onSubmit} defaultValues={defaultValues} resolver={resolver}>
        <Grid container flexDirection="column" rowSpacing={1} minWidth={220}>
          <Grid item xs>
            <CountrySelectFormInput name="country" required propId="countryCode" fullWidth />
          </Grid>
          <Grid item xs>
            <DatePickerFormInput
              views={views}
              name="accountingMonth"
              label={t('accountingEntries.columns.accountingMonth')}
              required
              maxDate={maxDate}
              minDate={minDate}
              centerFixed
            />
          </Grid>
        </Grid>
        <FlexSalesFormActions
          onCancel={handleCancel}
          cancelLabel={t('accountingParameters.labels.cancel')}
          submitLabel={t('monthClosing.labels.close')}
        />
      </FlexSalesForm>
    </PermissionsProvider>
  );
};

export default MonthClosingForm;
